import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useRoleHydration } from "../../hooks/useRoleHydration";
import { useCopilotDockOpen } from "../../hooks/useCopilotDockOpen";
import { useCopilotEnabled } from "../../hooks/useCopilotEnabled";
import { useMarketplaceGate } from "../../hooks/useMarketplaceGate";
import { useProHome } from "../../stores/proHome";
import { BookCapacityBanner } from "../billing/BookCapacityBanner";
import { UnusedDiscountBanner } from "../billing/UnusedDiscountBanner";
import { ProChatDock } from "../copilot/ProChatDock";
import { MarketplaceAccessPausedBanner } from "../marketplace/MarketplaceAccessPausedBanner";
import { NoStandingOffersBanner } from "../marketplace/NoStandingOffersBanner";
import { HeaderNav } from "./HeaderNav";

// SILL-512: the marketplace "no standing offers" banner is mounted ONCE here
// (not per-page) so it can't double-fetch or double-fire analytics across
// the marketplace sub-pages. It renders on the Marketplace routes only: the
// Home hero already covers an unreachable agent (plan 443). It is a no-op
// self-contained component everywhere else (see NoStandingOffersBanner's own
// visibility contract for the enabled/loaded/zero-active-offers/not-dismissed
// gating).
function shouldShowNoOffersBanner(pathname: string): boolean {
  return pathname === "/marketplace" || pathname.startsWith("/marketplace/");
}

// The unused-discount activation nudge mounts here once (same rationale as
// above) on EVERY page except: /pricing — the CTA's own destination, which
// already renders the full activation copy — and /welcome, where the
// one-time WelcomeDiscountModal owns the moment. The component itself is
// self-gating (incentive present, not dismissed this session, ActivationBanner
// not on screen — see UnusedDiscountBanner's visibility contract).
function shouldShowDiscountBanner(pathname: string): boolean {
  return pathname !== "/pricing" && pathname !== "/welcome";
}

// The book-capacity over-plan nudge shares the discount banner's mount rule:
// everywhere except the CTA's own destination and the welcome moment. The
// component itself gates on the resolved cap position (at/over cap, not
// dismissed this session — see BookCapacityBanner's visibility contract).
function shouldShowCapacityBanner(pathname: string): boolean {
  return shouldShowDiscountBanner(pathname);
}

/**
 * App shell — header-owned primary nav plus route-provided page/sidebar slots.
 *
 * Navigation shell note — deliberate trip-wire crossing:
 * The Engagement group (Campaigns · Automations) introduces a two-level
 * navigation shape: a primary header entry (60px tall) plus a sub-nav row
 * (38px, rendered by SubNav.tsx inside HeaderNav). This crosses the ProLayout
 * trip-wire ("nested nav → migrate to a rail") deliberately. The Engagement
 * group is a product-level feature grouping, not per-page chrome, and the
 * design shows a sub-nav strip within the same dark header surface — not a
 * separate rail. A rail migration would be the right path if PRIMARY
 * destinations exceeded seven OR if additional groups needed two-level nav;
 * today only Engagement needs it and the total primary-plus-group count stays
 * well under seven. The sidebar sticky-top offset is adjusted to 98px
 * (60px header + 38px sub-nav) when an Engagement sub-path is active.
 */
export function ProLayout({
  pageSidebar,
  pageMobileNav,
  children,
}: {
  pageSidebar?: ReactNode;
  /** Below `md` the page sidebar is hidden; this is its phone-width
   *  counterpart (a compact strip). Non-sticky, so it adds no offset math. */
  pageMobileNav?: ReactNode;
  children: ReactNode;
}) {
  // Drive the active viewing role from the agent's real profile.role (item 4).
  useRoleHydration();

  // Copilot dock open/closed state (D4 — persists across pages/sessions,
  // plan 188 W2-M8 task 802). Lifted here so both the HeaderNav opener pill
  // and the docked/drawer surface share one source of truth.
  const {
    open: copilotOpen,
    toggle: toggleCopilot,
    setOpen: setCopilotOpen,
  } = useCopilotDockOpen();

  // PostHog rollout / kill-switch for the whole copilot (dock + header pill).
  // When disabled, HeaderNav gets no opener handler (pill hidden) and the dock
  // never mounts. The `/assistant` route is gated separately in App.tsx.
  const copilotEnabled = useCopilotEnabled();

  const { pathname } = useLocation();

  // The shell owns its own chrome geometry and publishes it once; sticky
  // children (the page sidebar, and ProChatDock — a sibling) consume the
  // variables instead of re-deriving them. That indirection is the fix for a
  // real class of bug, twice over:
  //   * ProChatDock hardcoded `top-[60px]`, so on an Engagement path it sat
  //     under the 38px sub-nav AND overflowed the viewport by 38px.
  //   * Banners render BETWEEN the sticky header and the flex row, so at
  //     scroll 0 they push that row down by their own height; a child sized
  //     `100vh - headerH` then ends one banner-height BELOW the fold, which
  //     is how the copilot's "Ask Sill" composer became unreachable.
  // Both are MEASURED rather than constants: the header grows by the sub-nav
  // (and carries a sub-pixel border — the 98px constant this replaced was
  // off by 1), and the banners wrap to two lines at narrow widths and can
  // both show at once.
  const [chrome, setChrome] = useState({ header: 60, banner: 0 });

  // Paused marketplace access (verification banned/revoked) is an app-wide
  // condition, so the banner mounts on every route. It reads the SAME Home
  // store the header badge loads (one shared request); with the gate not `on`
  // the store issues no marketplace call and `home.data` stays null, so the
  // banner can never show. The pipeline summary is Home-only, so this
  // every-route mount does not load it.
  const marketplaceGate = useMarketplaceGate();
  const { home } = useProHome(marketplaceGate, { pipeline: false });
  const showPausedBanner =
    marketplaceGate === "on" && home.data?.setup?.access_paused === true;

  const showNoOffersBanner = shouldShowNoOffersBanner(pathname);
  const showDiscountBanner = shouldShowDiscountBanner(pathname);
  const showCapacityBanner = shouldShowCapacityBanner(pathname);
  const hasBanner =
    showPausedBanner ||
    showNoOffersBanner ||
    showDiscountBanner ||
    showCapacityBanner;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    // `:scope >` keeps this to the shell's OWN header — measured in place
    // rather than wrapped, because a wrapper div would become the sticky
    // containing block and break `position: sticky` on the header itself.
    const header =
      rootRef.current?.querySelector<HTMLElement>(":scope > header") ?? null;
    const banner = bannerRef.current;
    const measure = () => {
      setChrome((prev) => {
        const next = {
          // A MISSING header keeps the previous value rather than publishing
          // 0px. Publishing 0 would satisfy `var(--pro-header-h,60px)` — the
          // fallback would never fire — and every sticky child would get
          // top:0/height:100vh, silently restoring the overflow this measures
          // away. An absent BANNER genuinely means zero.
          header: header ? header.offsetHeight : prev.header,
          banner: banner ? banner.offsetHeight : 0,
        };
        return prev.header === next.header && prev.banner === next.banner
          ? prev
          : next;
      });
    };
    measure();

    // Watch the DOM, not React: the banners self-gate on their own fetched
    // state, so the wrapper is empty on first commit and fills in later — and
    // that commit belongs to the CHILD, so no parent effect re-runs and a
    // one-shot read stays 0 forever. MutationObserver also covers the sub-nav
    // mounting inside the header on navigation.
    const nodes = [header, banner].filter((n): n is HTMLElement => n !== null);
    const mos = nodes.map((n) => {
      const mo = new MutationObserver(measure);
      mo.observe(n, { childList: true, subtree: true });
      return mo;
    });
    // ResizeObserver already covers re-wrapping at a narrower viewport, so the
    // resize listener is strictly the FALLBACK where RO is absent (jsdom has
    // none) — not a second signal running alongside it.
    const ro =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (ro) {
      nodes.forEach((n) => ro.observe(n));
    } else {
      window.addEventListener("resize", measure);
    }
    return () => {
      mos.forEach((mo) => mo.disconnect());
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
    // Deliberately NOT keyed on pathname: this is the app shell, so that would
    // tear down and rebuild every observer plus force a synchronous
    // offsetHeight reflow on each SPA navigation. The nodes survive
    // navigation, and the MutationObserver already re-measures when the
    // sub-nav or a banner appears inside them.
  }, [hasBanner]);

  // ADR-034 dynamic-value carve-out: a NAMED CSS-variable style object, not a
  // static inline style. Consumed by the sticky page sidebar below and by
  // ProChatDock (a sibling), both of which must shorten by the banner height.
  const chromeOffsetStyle = useMemo(
    () =>
      ({
        "--pro-header-h": `${chrome.header}px`,
        "--pro-banner-h": `${chrome.banner}px`,
      }) as CSSProperties,
    [chrome],
  );

  return (
    <div
      ref={rootRef}
      className="min-h-screen w-full max-w-full bg-bg-warm text-fg"
      style={chromeOffsetStyle}
    >
      {/*
        The document owns vertical scrolling. Dense route content must opt into
        its own labeled scroll region instead of widening the document.
      */}
      <HeaderNav
        copilotOpen={copilotEnabled ? copilotOpen : undefined}
        onToggleCopilot={copilotEnabled ? toggleCopilot : undefined}
      />
      {hasBanner && (
        <div ref={bannerRef}>
          {showPausedBanner && <MarketplaceAccessPausedBanner />}
          {showNoOffersBanner && <NoStandingOffersBanner />}
          {showDiscountBanner && <UnusedDiscountBanner />}
          {showCapacityBanner && <BookCapacityBanner />}
        </div>
      )}
      {pageMobileNav && <div className="min-w-0 md:hidden">{pageMobileNav}</div>}
      <div className="flex min-w-0">
        {pageSidebar && (
          <aside
            className="sticky top-[var(--pro-header-h,60px)] hidden h-[calc(100vh-var(--pro-header-h,60px)-var(--pro-banner-h,0px))] w-56 shrink-0 overflow-y-auto border-r border-border-soft bg-[color-mix(in_oklch,var(--sill-bg-warm)_60%,var(--sill-card))] md:block"
          >
            {pageSidebar}
          </aside>
        )}
        <main className="flex min-w-0 flex-1 flex-col">{children}</main>
        {/* Docked panel (>=1280px) renders as a real flex sibling of `main`
            so the roster reflows beside it — never an overlay (scenario
            674). Below 1280px, `ProChatDock` renders nothing here and
            instead mounts as a Sheet-based overlay drawer (portal). */}
        {copilotEnabled && (
          <ProChatDock
            open={copilotOpen}
            onClose={() => setCopilotOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
